// components
import EntityOption from '../../components/EntityOption'
// constants
import { ENTITIES } from 'constants/entities'
// types
import { IOption } from 'components/FormElements/Select'
import { GroupBase } from 'react-select'
import {
  IBusinessActivity,
  IBusinessProcess,
  IBusinessUnit,
  ICompany,
  IField,
  IFieldOption,
  ILocation,
  IObjectEntity,
  IObjectSecurity,
  IPlan,
  IPlanItem,
  IPlanType,
  IPosition,
  IRiskTemplate,
  ISecurityProfile,
  IUser
} from 'types/graphql'
// utils
import { get } from 'lodash'
import { filter, find, isString, map, pipe, getOr } from 'lodash/fp'

export const companyProps = {
  getOptions: ({ data }) =>
    get<ICompany[]>(data, 'companies.data', []).map(({ id, name }) => ({
      value: id,
      label: name
    })),
  handleSearch: ({ search, refetch }) => refetch({ filter: { name: search } })
}

export const positionProps = {
  getOptions: ({ data }) =>
    get<IPosition[]>(data, 'positions.data', [])
      .filter(({ active }) => active)
      .map(({ id, description }) => ({
        value: id,
        label: description
      })),
  handleSearch: ({ search, refetch }) => refetch({ filter: { description: search } })
}

export const userProps = {
  getOptions: ({ data }) =>
    get<IUser[]>(data, 'users.data', [])
      .filter(({ active }) => active)
      .map(({ id, firstName, lastName, displayName }) => ({
        label: displayName || `${firstName} ${lastName}`,
        value: id
      })),
  handleSearch: ({ search, refetch }) => refetch({ filter: { name: search } })
}

export const planProps = {
  getOptions: ({ data }) =>
    get<IPlan[]>(data, 'plans.data', []).map(({ id, name }) => ({
      label: name,
      value: id
    })),
  handleSearch: ({ search, refetch }) => refetch({ filter: { name: search } })
}

export const businessUnitProps = {
  getOptions: ({ data }) =>
    get<IBusinessUnit[]>(data, 'businessUnits.data', [])
      .filter(({ active }) => active)
      .map(({ id, description }) => ({
        value: id,
        label: description
      })),
  handleSearch: ({ search, refetch }) => refetch({ filter: { description: search } })
}

export const businessActivityProps = {
  getOptions: ({ data }) =>
    get<IBusinessActivity[]>(data, 'businessActivities.data', [])
      .filter(({ active }) => active)
      .map(({ id, description }) => ({
        value: id,
        label: description
      })),
  handleSearch: ({ search, refetch }) => refetch({ filter: { description: search } })
}

export const businessProcessProps = {
  getOptions: ({ data }) =>
    get<IBusinessProcess[]>(data, 'businessProcesses.data', [])
      .filter(({ active }) => active)
      .map(({ id, description }) => ({
        value: id,
        label: description
      })),
  handleSearch: ({ search, refetch }) => refetch({ filter: { description: search } })
}

export const planTypeProps = {
  getOptions: ({ data }) =>
    get<IPlanType[]>(data, 'planTypes.data', [])
      .filter(({ active }) => active)
      .map(({ id, description }) => ({
        value: id,
        label: description,
        entity: ENTITIES.PLAN_TYPE
      })),
  handleSearch: ({ search, refetch }) => refetch({ filter: { description: search } })
}

export const planTreeProps = {
  getOptions: ({ data, planId }: { data: any; planId: string }) => {
    const entities = [
      { path: 'planObjectives.data', entity: ENTITIES.PLAN_OBJECTIVE },
      { path: 'planKPIs.data', entity: ENTITIES.PLAN_KPI },
      { path: 'planActions.data', entity: ENTITIES.PLAN_ACTION }
    ]

    return entities.flatMap(({ path, entity }) =>
      get<any[]>(data, path, []).map(({ description, planItems }) => ({
        value: pipe<any, IPlanItem[], IPlanItem[], string>(
          getOr([], 'data'),
          filter((item) => item.planId === planId),
          getOr('', '[0].id')
        )(planItems),
        label: description,
        entity
      }))
    )
  },
  handleSearch: ({ variables, search, refetch }) => {
    const planId: string = get(variables, 'objectiveFilter.planId')

    if (planId && isString(search)) {
      return refetch({
        objectiveFilter: { planId, description: search },
        actionFilter: { planId, description: search },
        kpiFilter: { planId, description: search }
      })
    }
  },
  components: {
    Option: EntityOption
  }
}

export const userAndPositionProps = {
  getOptions: (args: any) => {
    const userOptions = userProps.getOptions(args).map((option) => ({ ...option, entity: ENTITIES.USER }))
    const positionOptions = positionProps.getOptions(args).map((option) => ({ ...option, entity: ENTITIES.POSITION }))

    return [...userOptions, ...positionOptions]
  },
  handleSearch: ({ search, refetch }) =>
    refetch({ userFilter: { name: search }, positionFilter: { description: search } }),
  components: {
    Option: EntityOption
  }
}

export const planStatusProps = {
  getOptions: ({ data }) =>
    pipe<any, IPlanType[], GroupBase<IOption>[]>(
      getOr([], 'planTypes.data'),
      map(({ description, fields }) => {
        const options = pipe<any, IField, IFieldOption[], IFieldOption[], IOption[]>(
          find((field: IField) => field.name === 'status'),
          getOr([], 'options'),
          filter((option) => !!option.label),
          map(({ id, label }) => ({ value: id, label: `${description} - ${label}` }))
        )(fields)

        return {
          label: description,
          options
        }
      })
    )(data),
  handleSearch: ({ search, refetch }) => refetch({ filter: { description: search } })
}

export const objectsProps = {
  getOptions: ({ data }) =>
    get<IObjectEntity[]>(data, 'objectEntities.data', []).map(({ apiName, name }) => ({
      value: apiName,
      label: name
    }))
}

export const securityProfileProps = {
  getOptions: ({ data }) =>
    get<ISecurityProfile[]>(data, 'securityProfiles.data', [])
      .filter(({ active }) => active)
      .map(({ id, name }) => ({
        value: id,
        label: name
      })),
  handleSearch: ({ search, refetch }) => refetch({ filter: { name: search } })
}

export const objectSecurityProps = {
  getOptions: ({ data }) =>
    get<IObjectSecurity[]>(data, 'objectSecurities.data', []).map(({ id, name }) => ({
      value: id,
      label: name
    }))
}

export const riskTemplatesProps = {
  getOptions: ({ data }) =>
    get<IRiskTemplate[]>(data, 'riskTemplates.data', []).map(({ id, name }) => ({
      value: id,
      label: name
    })),
  handleSearch: ({ search, refetch }) => refetch({ filter: { name: search } })
}

export const locationProps = {
  getOptions: ({ data }) =>
    get<ILocation[]>(data, 'locations.data', [])
      .filter(({ active }) => active)
      .map(({ id, description }) => ({
        value: id,
        label: description
      })),
  handleSearch: ({ search, refetch }) => refetch({ filter: { description: search } })
}
